import PaymentMethodSelector from '@/components/shared/PaymentMethodSelector/PaymentMethodSelector';
import { Input } from '@/components/shared/Input/Input';
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import StatusSelector from '@/components/shared/StatusSelector/StatusSelector';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SellsService } from '@/services/sells.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { useSoldProductFilterOptions } from '@/hooks/useSoldProductFilterOptions';
import { fetchReportTemplateOptions } from '@/utils/checkoutTemplates';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const PAYMENT_STATUS = [
  'paid',
  'awaiting_payment',
  'not_authorized',
  'chargeback',
  'refunded',
] as const;

const FilterSchema = z.object({
  products: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  offers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  affiliates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  coproducers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  templates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  payment_methods: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  src: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
});

type FilterFormData = z.infer<typeof FilterSchema>;

export default function FilterSalesByStatus({
  onFilter,
  filterValues,
}: {
  filterValues: FilterFormData;
  onFilter: (data: FilterFormData) => void;
}) {
  const { t } = useTranslation(['sales', 'common', 'reports']);
  const { closeModal } = useModalStore();
  const { currency } = useCurrencyStore();

  const { data: soldPaymentMethods = [] } = useQuery({
    queryKey: ['sold-payment-methods', currency],
    queryFn: () => SellsService.getSoldPaymentMethods(currency),
    staleTime: 60_000,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    getValues,
  } = useForm<FilterFormData>({
    shouldUnregister: false,
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      templates: filterValues?.templates || [],
      payment_methods: filterValues?.payment_methods || [],
      status: filterValues?.status || Array.from(PAYMENT_STATUS),
      src: filterValues?.src || undefined,
      utm_source: filterValues?.utm_source || undefined,
      utm_medium: filterValues?.utm_medium || undefined,
      utm_campaign: filterValues?.utm_campaign || undefined,
      utm_content: filterValues?.utm_content || undefined,
      utm_term: filterValues?.utm_term || undefined,
    },
  });

  const defaultValues = watch();

  useEffect(() => {
    if (filterValues?.payment_methods?.length) return;
    if (soldPaymentMethods.length === 0) return;
    const types = soldPaymentMethods.map(m => m.type);
    const current = watch('payment_methods') ?? [];
    const same =
      current.length === types.length &&
      types.every(t => current.includes(t));
    if (!same) {
      setValue('payment_methods', types);
    }
  }, [soldPaymentMethods, filterValues?.payment_methods]);

  const {
    productIds,
    productIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchAffiliatesOptions,
    fetchCoProducersOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: 'sales-by-status',
    enableAffiliates: true,
    enableCoproducers: true,
  });

  const onSubmit = async (data: any) => {
    onFilter(data);
    closeModal('filter-sales-status');
  };

  const handleSelectAll = <T extends keyof FilterFormData>(
    field: T,
    items: FilterFormData[T]
  ) => {
    const currentValues = defaultValues[field] ?? [];
    const isSelectedAll =
      Array.isArray(currentValues) && currentValues.length === items.length;

    setValue(field, isSelectedAll ? ([] as any) : items);
  };

  return (
    <section className='h-full flex relative flex-col justify-between'>
      <SheetHeader className='md:p-6 p-4'>
        <SheetTitle className='flex gap-2'>{t('filters')}</SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col justify-between h-full'
      >
        <section className='flex  relative flex-col gap-4 px-4  md:px-6'>
          <MultiComboBox
            label={t('products')}
            placeholder={t('products_placeholder')}
            fetchOptions={fetchProductOptions}
            values={watch('products')}
            onChange={(value) => {
              handleProductsChange(value);
              clearErrors('products');
            }}
            helperText={errors.products?.message}
            queryKey='sold-products'
            returnFullObject
            cypressName='multicombobox-products'
            selectAllOptions
            newParams={[currency]}
          />

          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t('offer')}
            placeholder={t('offer_placeholder')}
            fetchOptions={fetchOfferOptions}
            values={watch('offers')}
            onChange={(values) => {
              handleOffersChange(values);
              clearErrors('offers');
            }}
            helperText={errors.offers?.message}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName='multicombobox-offers'
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t('affiliate')}
            placeholder={t('affiliate_placeholder')}
            fetchOptions={fetchAffiliatesOptions}
            values={watch('affiliates')}
            onChange={(value) => {
              setValue('affiliates', value);
              clearErrors('affiliates');
            }}
            helperText={errors.affiliates?.message}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName='multicombobox-affiliates'
            selectAllOptions
            newParams={productIds}
          />

          <MultiComboBox
            key={`coproducers-${productIdsKey}`}
            label={t('co_producer')}
            placeholder={t('co_producer_placeholder')}
            fetchOptions={fetchCoProducersOptions}
            values={watch('coproducers')}
            onChange={(value) => {
              setValue('coproducers', value);
              clearErrors('coproducers');
            }}
            helperText={errors.coproducers?.message}
            queryKey={queryKeys.coproducers}
            returnFullObject
            cypressName='multicombobox-coproducers'
            selectAllOptions
            newParams={productIds}
          />

          <MultiComboBox
            label={t('reports:template')}
            placeholder={t('reports:select_template')}
            fetchOptions={fetchReportTemplateOptions}
            values={watch('templates')}
            onChange={(value) => {
              setValue('templates', value);
              clearErrors('templates');
            }}
            helperText={errors.templates?.message}
            queryKey='report-templates-sales-status'
            returnFullObject
            cypressName='multicombobox-templates'
            selectAllOptions
          />

          <Separator className='my-4' />

          <PaymentMethodSelector
            variant="compact"
            availableMethods={soldPaymentMethods}
            selectedMethods={defaultValues.payment_methods ?? []}
            onChange={(methods) => {
              setValue('payment_methods', methods);
              clearErrors('payment_methods');
            }}
            emptyMessage={t('common:payment_methods_no_history')}
          />

          <Separator className='my-4' />

          <div className='space-y-4'>
            <div className='flex items-center justify-between gap-2'>
              <h2 className='text-lg font-semibold'>{t('payment_status')}</h2>
              <span
                className='text-sm font-medium text-primary hover:text-primary/80 cursor-pointer'
                onClick={() => handleSelectAll('status', Array.from(PAYMENT_STATUS))}
              >
                {t('select_all')}
              </span>
            </div>
            <StatusSelector
              options={PAYMENT_STATUS.map(status => ({ value: status, label: t(`common:${status}`) }))}
              selectedOptions={defaultValues.status ?? []}
              onChange={(statuses) => {
                setValue('status', statuses);
                clearErrors('status');
              }}
              variant="compact"
            />
          </div>

          <Separator className='my-4' />

          <div className='space-y-4 mb-24'>
            <h2 className='text-lg font-semibold'>{t('url_parameters')}</h2>
            <Input
              {...register('src')}
              label={t('src')}
              placeholder={t('src_placeholder')}
              helperText={errors.src?.message}
              data-cy='input-src'
            />
            <Input
              {...register('utm_source')}
              label={t('utm_source')}
              placeholder={t('utm_source_placeholder')}
              helperText={errors.utm_source?.message}
              data-cy='input-utm-source'
            />
            <Input
              {...register('utm_medium')}
              label={t('utm_medium')}
              placeholder={t('utm_medium_placeholder')}
              helperText={errors.utm_medium?.message}
              data-cy='input-utm-medium'
            />
            <Input
              {...register('utm_campaign')}
              label={t('utm_campaign')}
              placeholder={t('utm_campaign_placeholder')}
              helperText={errors.utm_campaign?.message}
              data-cy='input-utm-campaign'
            />
            <Input
              {...register('utm_content')}
              label={t('utm_content')}
              placeholder={t('utm_content_placeholder')}
              helperText={errors.utm_content?.message}
              data-cy='input-utm-content'
            />
            <Input
              {...register('utm_term')}
              label={t('utm_term')}
              placeholder={t('utm_term_placeholder')}
              helperText={errors.utm_term?.message}
              data-cy='input-utm-term'
            />
          </div>
        </section>

        <SheetFooter className='flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end'>
          <div className='flex w-full z-50 gap-2 justify-end items-end'>
            <Button
              variant='ghost'
              className='w-full !text-colorText md:max-w-[118px]'
              onClick={() => closeModal('filter-sales-status')}
              type='button'
            >
              {t('cancel')}
            </Button>
            <Button
              variant='default'
              className='w-full md:max-w-fit'
              type='submit'
              data-cy='apply-filters'
            >
              {t('apply_filters')}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
